import React, { useState, useEffect } from 'react';
import * as LucideIcons from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';

import { useAppContext } from '../AppContext';
import NexusCard from './NexusCard';
import { useTranslation } from 'react-i18next';

function RecommendedNexusCard({ recommendation }) {
    const { db, appId } = useAppContext();
    const [nexus, setNexus] = useState(null);

    useEffect(() => {
        const fetchNexus = async () => {
            const nexusRef = doc(db, `artifacts/${appId}/public/data/nexuses`, recommendation.nexusId);
            const docSnap = await getDoc(nexusRef);
            if (docSnap.exists()) {
                setNexus({ id: docSnap.id, ...docSnap.data() });
            }
        };
        fetchNexus();
    }, [recommendation.nexusId, doc, getDoc, db, appId]);

    if (!nexus) return null;

    return (
        <div className="bg-gray-800/70 p-3 rounded-lg border border-gray-700">
            <NexusCard nexus={nexus} />
            <blockquote className="mt-2 border-l-4 border-purple-400 pl-3 text-sm italic text-gray-300">
                "{recommendation.reason}"
            </blockquote>
        </div>
    );
}

function NexusFinder() {
    const { t } = useTranslation();
    const { appFunctions, setMessage, showConfirmation, LucideIcons } = useAppContext();
    const [recommendations, setRecommendations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleFind = () => {
        setError('');
        setRecommendations([]);
        showConfirmation({
            message: t('nexusFinder.confirmation'),
            onConfirm: async () => {
                setIsLoading(true);
                const getNexusRecommendations = httpsCallable(appFunctions, 'getNexusRecommendations');
                try {
                    const result = await getNexusRecommendations();
                    setRecommendations(result.data.recommendations || []);
                } catch (err) {
                    console.error("Error getting recommendations:", err);
                    setError(err.message);
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    return (
        <div className="p-4 bg-gradient-to-br from-purple-900/50 to-blue-900/50 rounded-lg shadow-lg mb-6 text-center border border-purple-500/50">
            <h3 className="text-xl font-bold text-white font-playfair mb-2">{t('nexusFinder.title')}</h3>
            <p className="text-gray-300 mb-4">{t('nexusFinder.description')}</p>
            <button onClick={handleFind} disabled={isLoading} className="small-action-button bg-purple-600 hover:bg-purple-700 text-lg py-3 px-6 disabled:opacity-50">
                {isLoading ? <div className="action-spinner" /> : <><LucideIcons.BrainCircuit size={18} className="mr-2" /> {t('nexusFinder.findButton')}</>}
            </button>

            {error && <p className="text-red-400 mt-4">{error}</p>}

            {recommendations.length > 0 && (
                <div className="mt-6 text-left space-y-3 animate-fadeIn">
                    <h4 className="font-bold text-center">{t('nexusFinder.recommendationsHeader')}</h4>
                    {recommendations.map(rec => <RecommendedNexusCard key={rec.nexusId} recommendation={rec} />)}
                </div>
            )}
        </div>
    );
}

export default NexusFinder;
